import moment from '../moment';
import MomentTZ from 'moment-timezone';
import type { Moment } from 'moment';
import { isEmpty } from 'lodash-es';
import { weekdays } from '@phoenix/helpers/date-helper';

type OpeningInterval = {
  start: string;
  end: string;
};

type OpeningDay = {
  openIntervals: OpeningInterval[];
};

type OpeningHours = Record<string, OpeningDay>;

type SpecialDay = {
  date: string;
  endDate?: string;
  isClosed: boolean;
  openIntervals: OpeningInterval[];
};

type StoreTranslations = {
  t: (key: string, params?: Record<string, string>) => string;
};

type StoreStatus = {
  status: string;
  label: string;
};

type NextOpeningDayAndHour = {
  day: number;
  hour: string;
};

export default class Store {
  id: string;
  openingHours: OpeningHours;
  specialDays: SpecialDay[];
  timezone: string;
  storeDate: Moment;
  statusElement: HTMLElement | null;
  statusOpenElement: HTMLElement | null;
  days: Record<number, string>;
  translations: StoreTranslations;
  hourFormat: string;
  isOnline: boolean;
  isComingSoon: boolean;
  status: StoreStatus;

  constructor(
    ...[openingHours, specialDays = [], timezone, isOnline = true, id = '', isComingSoon = false]: [
      OpeningHours,
      SpecialDay[] | undefined,
      string,
      boolean?,
      string?,
      boolean?,
    ]
  ) {
    this.id = id;
    this.openingHours = openingHours;
    this.specialDays = specialDays;
    this.timezone = timezone === 'JST' ? 'Japan' : timezone;
    this.storeDate = MomentTZ(moment()).tz(this.timezone);
    this.translations = window.Phoenix.i18n;
    this.isOnline = isOnline;
    this.isComingSoon = isComingSoon;
    this.hourFormat = window.Phoenix.currentCountry === 'US' ? 'h:mm a' : 'H.mm';
    this.days = weekdays;
    this.status = this.getStatus();
    this.statusElement = null;
    this.statusOpenElement = null;

    if (id && !this.isOnline) {
      document.querySelectorAll('.store-opening-status').forEach((el) => (el.textContent = this.status.label));

      this.statusElement = document.getElementById(`status-${this.id}`);
      this.statusOpenElement = document.getElementById(`status-${this.id}-open`);

      if (this.statusOpenElement && this.status.status === 'open') {
        this.statusOpenElement.textContent = 'open';
        this.statusOpenElement.classList.remove('hidden');
      } else if (this.statusElement) {
        this.statusElement.textContent = this.status.label;
      }
    }
  }

  getStatus(): StoreStatus {
    if (this.isComingSoon) {
      return {
        status: 'comingSoon',
        label: this.translations.t('Store.boutique.comingSoon'),
      };
    }

    if (this.isOnline) {
      return {
        status: 'online',
        label: '',
      };
    }

    if (isEmpty(this.openingHours) || !this.timezone) {
      return {
        status: 'closed',
        label: this.translations.t('Store.closed'),
      };
    }

    const dayIndex = this.storeDate.isoWeekday();
    let [closingHour, openingHour] = this.getClosingOrOpeningHour(this.openingHours[this.days[dayIndex]].openIntervals);

    const specialDay = this.specialDays.find((specialDay) => this.isSpecialDayForDate(this.storeDate, specialDay));
    if (specialDay) {
      [closingHour, openingHour] = specialDay.isClosed
        ? [null, null]
        : this.getClosingOrOpeningHour(specialDay.openIntervals);
    }

    // If we don't have a closing hour but an opening hour, it means that the store is going to be open later that day.
    if (!closingHour && openingHour) {
      return {
        status: 'openingSoon',
        label: this.translations.t('Store.status.closedButWillOpen', {
          startHour: openingHour.format(this.hourFormat),
        }),
      };
    }

    // If we have a closing hour here, it means that the store is currently open.
    if (closingHour) {
      return {
        status: 'open',
        label: this.translations.t('Store.status.open', { endHour: closingHour.format(this.hourFormat) }),
      };
    }

    const nextOpeningDayAndHour = this.getNextOpeningDayAndHour();

    if (nextOpeningDayAndHour) {
      return {
        status: 'closed',
        label: this.translations.t('Store.status.closed', {
          day: this.translations.t(`Weekday.${nextOpeningDayAndHour.day}`).toLowerCase(),
          startHour: nextOpeningDayAndHour.hour,
        }),
      };
    }

    return {
      status: 'closed',
      label: this.translations.t('Store.closed'),
    };
  }

  getStatusLabel(): string {
    return this.status.label;
  }

  getClosingOrOpeningHour(openIntervals: OpeningInterval[]): [Moment | null, Moment | null] {
    let closingHour: Moment | null = null;
    let openingHour: Moment | null = null;
    openIntervals.forEach((interval) => {
      const [startHours, startMinutes] = interval.start.split(':');
      const start = moment(this.storeDate).set({
        hours: Number(startHours),
        minutes: Number(startMinutes),
      });
      const [endHours, endMinutes] = interval.end.split(':');
      const end = moment(this.storeDate).set({
        hours: Number(endHours),
        minutes: Number(endMinutes),
      });

      if (end.isBefore(start)) {
        end.add(1, 'd');
      }

      if (this.storeDate.isAfter(start) && this.storeDate.isBefore(end)) {
        closingHour = end;
      } else if (this.storeDate.isBefore(start) && !openingHour) {
        openingHour = start;
      }
    });
    return [closingHour, openingHour];
  }

  getNextOpeningDayAndHour(): NextOpeningDayAndHour | null {
    const nextDate = this.storeDate;
    const dayIndex = nextDate.isoWeekday();

    do {
      nextDate.add(1, 'days');

      const specialDaysForNextDate = this.specialDays.filter((specialDay) =>
        this.isSpecialDayForDate(nextDate, specialDay),
      );

      if (!(specialDaysForNextDate.length > 0 && specialDaysForNextDate[0].isClosed)) {
        const nextDayOpeningHours = specialDaysForNextDate[0]
          ? specialDaysForNextDate[0].openIntervals
          : this.openingHours[this.days[nextDate.isoWeekday()]].openIntervals;

        if (nextDayOpeningHours.length > 0) {
          return {
            day: nextDate.isoWeekday(),
            hour: nextDayOpeningHours[0].start,
          };
        }
      }
    } while (nextDate.isoWeekday() !== dayIndex);

    return null;
  }

  isSpecialDayForDate(date: Moment, specialDay: SpecialDay): boolean {
    const format = 'YYYY-MM-DD';
    return !specialDay.endDate
      ? specialDay.date === date.format(format)
      : date.isBetween(moment(specialDay.date, format), moment(specialDay.endDate, format).add(1, 'd'));
  }
}
